import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCheckCircle,
  faMicrophoneAlt,
  faNotesMedical,
  faPenNib,
  faWandMagicSparkles,
} from "@fortawesome/pro-solid-svg-icons";

export interface MilestoneConfig {
  key: string;
  label: string;
  icon: IconDefinition;
  /** Action for "Show Me How": 'ftue' starts the tour, a string path navigates, null means passive */
  showMeHow: string | null;
}

const TEMPLATE_CREATED: MilestoneConfig = {
  key: "template_created",
  label: "Create a template that scribes like you",
  icon: faWandMagicSparkles,
  showMeHow: "/templates/create",
};

const FIRST_NOTE_RECORDED: MilestoneConfig = {
  key: "first_note_recorded",
  label: "Record your first note",
  icon: faMicrophoneAlt,
  showMeHow: "ftue",
};

const TEMPLATE_FIRST: MilestoneConfig[] = [
  TEMPLATE_CREATED,
  FIRST_NOTE_RECORDED,
];

const NOTE_FIRST: MilestoneConfig[] = [
  FIRST_NOTE_RECORDED,
  TEMPLATE_CREATED,
];

const REMAINING_MILESTONES: MilestoneConfig[] = [
  {
    key: "note_approved",
    label: "Review and mark a note as done",
    icon: faCheckCircle,
    showMeHow: "review-tour",
  },
  {
    key: "note_exported",
    label: "Export a note into your PIMS",
    icon: faPenNib,
    showMeHow: "export-tour",
  },
  {
    key: "three_day_notes",
    label: "Created notes on 3 different days",
    icon: faNotesMedical,
    showMeHow: null,
  },
];

/** Default order (template first) — used for completion count, allComplete, etc. */
export const MILESTONES: MilestoneConfig[] = [
  ...TEMPLATE_FIRST,
  ...REMAINING_MILESTONES,
];

export function getMilestones(
  swapOrder: boolean,
  includeTemplate = true,
): MilestoneConfig[] {
  const first = swapOrder ? NOTE_FIRST : TEMPLATE_FIRST;
  return [
    ...(includeTemplate
      ? first
      : first.filter((m) => m.key !== "template_created")),
    ...REMAINING_MILESTONES,
  ];
}

const TEMPLATE_FIRST_PROMPTS = [
  {
    bold: "Let's go!",
    rest: "Create a template to get started.",
  },
  {
    bold: "Template created!",
    rest: "Ready to record your first note?",
  },
];

const NOTE_FIRST_PROMPTS = [
  {
    bold: "Let's go!",
    rest: "Record your first note to get started.",
  },
  {
    bold: "First note recorded!",
    rest: "Now create a template that scribes like you.",
  },
];

const REMAINING_PROMPTS = [
  {
    bold: "Great progress!",
    rest: "Review a note and mark it as done.",
  },
  {
    bold: "You're on a roll!",
    rest: "Export a note to your PIMS.",
  },
  {
    bold: "Almost there!",
    rest: "Keep recording on different days to complete onboarding!",
  },
];

const FREE_USER_PROMPTS = [
  {
    bold: "Let's go!",
    rest: "Record your first note to get started.",
  },
  ...REMAINING_PROMPTS,
];

export function getMilestonePrompts(
  swapOrder: boolean,
  includeTemplate = true,
): { bold: string; rest: string }[] {
  if (!includeTemplate) return FREE_USER_PROMPTS;
  return [
    ...(swapOrder ? NOTE_FIRST_PROMPTS : TEMPLATE_FIRST_PROMPTS),
    ...REMAINING_PROMPTS,
  ];
}
